'use client';

import { createContext, use, type ReactNode } from 'react';

/**
 * Carries the value-confirmation controls (LF-4356, LF-5546) from
 * `InventoryFormCard` down to the value component that renders the value pair,
 * so each control sits directly under the figure it vouches for instead of
 * being stranded at the bottom of the card.
 *
 * Two independent slots are carried: `current` (Current Value / Current
 * Balance) and `dod` (Date-of-Death value / Amount Owed). `ValuePairFields`
 * (used by the standard asset/liability forms) consumes this and renders the
 * matching slot beneath each value. Surfaces that don't opt into value
 * confirmation (AI review, create-matter, title-search) provide `null` nodes,
 * so the slots render nothing.
 */
export interface ValueConfirmationSlots {
  current: ReactNode;
  dod: ReactNode;
}

const ValueConfirmationSlotContext = createContext<ValueConfirmationSlots>({
  current: null,
  dod: null
});

export const ValueConfirmationSlotProvider =
  ValueConfirmationSlotContext.Provider;

/** Renders the confirmation control provided by `InventoryFormCard`, if any. */
export function ValueConfirmationSlot({
  kind = 'current'
}: {
  kind?: 'current' | 'dod';
}) {
  const slots = use(ValueConfirmationSlotContext);
  return <>{kind === 'dod' ? slots.dod : slots.current}</>;
}
